import { DragEvent, useRef, useState } from "react";

// Page-level file drop: spread `dropHandlers` on the page's root element and
// render a drop overlay while `dragging`. Pass nothing to turn it off.
// dragenter/dragleave fire for every child element, so a counter (not a
// boolean) tells when the pointer really left the page.
export function usePageFileDrop(onFiles?: (files: File[]) => void) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes("Files");
  // A modal rendered inside the page (its own drop zone handles itself).
  const inDialog = (e: DragEvent) => Boolean((e.target as HTMLElement | null)?.closest?.("dialog"));

  const dropHandlers = onFiles
    ? {
        onDragEnter: (e: DragEvent) => {
          if (!hasFiles(e) || inDialog(e)) return;
          e.preventDefault();
          depth.current += 1;
          setDragging(true);
        },
        onDragOver: (e: DragEvent) => {
          if (!hasFiles(e) || inDialog(e)) return;
          e.preventDefault(); // required for the drop event to fire
          e.dataTransfer.dropEffect = "copy";
        },
        onDragLeave: (e: DragEvent) => {
          if (!hasFiles(e) || inDialog(e)) return;
          depth.current = Math.max(0, depth.current - 1);
          if (depth.current === 0) setDragging(false);
        },
        onDrop: (e: DragEvent) => {
          if (!hasFiles(e) || inDialog(e)) return;
          e.preventDefault();
          depth.current = 0;
          setDragging(false);
          const dropped = Array.from(e.dataTransfer.files);
          if (dropped.length > 0) onFiles(dropped);
        },
      }
    : {};

  return { dragging, dropHandlers };
}
